/** Route slug ↔ this module (`/real-estate/land-purchase-verification`). */
export const LAND_PURCHASE_AND_VERIFICATION_SLUG = "land-purchase-verification";

/**
 * CMS-ready defaults: icons are lucide-export names (strings).
 * When the API returns a payload, deep-merge into this shape server-side or in a thin adapter.
 */
export const landPurchaseVerificationDefaultData = {
  heroImage:
    "https://images.unsplash.com/photo-1570129477492-45c003edd2be?auto=format&fit=crop&w=1500&q=80",
  galleryImages: [
    "https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&w=900&q=80",
    "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=900&q=80",
    "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=900&q=80",
  ],
  stats: [
    { value: "3+", label: "Guides Available" },
    { value: "24h", label: "Expert Response" },
    { value: "5K+", label: "Ghanaians Helped" },
  ],
  introduction: {
    headline: "Why Land Law Matters in Ghana",
    body: "Land is one of the most valuable assets in Ghana — but also one of the most risky to invest in. Many people lose money due to fraud, buy from the wrong owner, face multiple ownership disputes, or fail to register their land properly.",
    keyFacts: [
      "A large portion of land in Ghana is not properly registered, increasing dispute risk",
      "Land disputes are among the most common legal cases in Ghanaian courts",
    ],
    callout: "Proper legal guidance is critical before, during, and after buying land.",
  },
  ownershipTypes: [
    {
      title: "Customary Land",
      subtitle: "Stool / Family Land",
      desc: "Owned by chiefs, families, or clans. The most common land type in Ghana, governed by traditional laws and customs.",
      tag: "Most common in Ghana",
      icon: "Users",
    },
    {
      title: "State Land",
      subtitle: "Government Owned",
      desc: "Owned and administered by the Government of Ghana. Acquisition and use are regulated by formal statutory law.",
      tag: "Government regulated",
      icon: "Landmark",
    },
    {
      title: "Private Land",
      subtitle: "Individual / Corporate",
      desc: "Owned by individuals or companies. The most straightforward land type for direct private transactions.",
      tag: "Clearest title",
      icon: "Building2",
    },
  ],
  verificationSteps: [
    {
      number: 1,
      icon: "Search",
      title: "Conduct a Land Search",
      desc: "Visit the Lands Commission to verify the true owner, whether land is already sold, any existing disputes or court orders, and government acquisition status.",
      tip: "Your first and most important line of protection.",
    },
    {
      number: 2,
      icon: "User",
      title: "Verify the Seller",
      desc: "Confirm the seller has legal authority. For family land, confirm with principal family members. For stool land, confirm directly with the chief.",
      tip: "Never pay without officially confirming seller identity.",
    },
    {
      number: 3,
      icon: "FileText",
      title: "Check Documentation",
      desc: "Review the Indenture (ownership document), Site plan (survey plan with GPS coordinates), and Allocation note for stool or family land grants.",
      tip: "Insist on originals or certified copies only.",
    },
    {
      number: 4,
      icon: "Scale",
      title: "Engage a Lawyer",
      desc: "A qualified property lawyer reviews all documents, drafts agreements, and protects your legal interest throughout the entire transaction.",
      tip: "Legal fees are worth every pesewa you spend.",
    },
  ],
  commonMistakes: [
    "Paying without prior verification at Lands Commission",
    "Trusting verbal promises without written documentation",
    "Buying land without proper ownership papers",
    "Ignoring or skipping official survey plans",
  ],
  registrationSteps: [
    { step: 1, title: "Conduct Official Search", desc: "Confirm ownership at Lands Commission before beginning registration." },
    { step: 2, title: "Prepare Your Documents", desc: "Indenture, survey plan, valid national ID, and stamp duty receipt." },
    { step: 3, title: "Submit to Lands Commission", desc: "All documents are reviewed and processed by the registration division." },
    { step: 4, title: "Publication Period", desc: "Your claim is published publicly for a set period to allow legitimate objections." },
    { step: 5, title: "Title Certificate Issued", desc: "You receive your official land title certificate — legal proof of ownership." },
  ],
  registrationSystems: [
    {
      name: "Title Registration",
      desc: "Stronger — government-guaranteed protection of ownership. Recommended for all buyers.",
      recommended: true,
    },
    {
      name: "Deed Registration",
      desc: "Weaker — only records the transaction without guaranteeing title. Basic protection only.",
      recommended: false,
    },
  ],
  disputeCauses: [
    "Multiple sale of the same land to different buyers",
    "Family ownership conflicts and inheritance disputes",
    "Boundary disagreements with neighbouring plots",
    "Fraudulent or forged ownership documents",
    "Government compulsory acquisition without proper notice",
  ],
  disputeActions: [
    "Do NOT use force under any circumstance",
    "Gather all documents and evidence immediately",
    "Contact a qualified property lawyer right away",
    "Consider mediation or Alternative Dispute Resolution (ADR) first",
    "Go to court only if mediation fails or the matter is severe",
  ],
  leaseTerms: [
    { who: "Foreigners", duration: "Up to 50 years", renewable: "Yes" },
    { who: "Ghanaian Citizens", duration: "Up to 99 years", renewable: "Yes" },
  ],
  leaseRequirements: [
    "Full legal names and identities of all parties",
    "Duration and specific start date of the lease",
    "Payment terms, amounts, and schedule",
    "Permitted land use and any restrictions",
    "Renewal conditions and procedures",
  ],
  leaseWatchOut: [
    "Expired lease with no clear renewal process",
    "Hidden or buried restrictive clauses in the agreement",
    "Incorrect or vague land boundary descriptions",
    "Unapproved or restricted land use categories",
  ],
  institutions: [
    {
      name: "Lands Commission",
      desc: "Central body that handles all land registration and maintains official records across Ghana.",
      icon: "Landmark",
      linkHref: "https://www.lc.gov.gh/",
      linkLabel: "lc.gov.gh",
    },
    {
      name: "Survey & Mapping Division",
      desc: "Responsible for producing all official site plans and land survey documentation in Ghana.",
      icon: "MapPin",
      linkHref: null,
      linkLabel: "See Lands Commission portals",
    },
    {
      name: "Town & Country Planning",
      desc: "Confirms zoning regulations, land use classifications, and development permit requirements.",
      icon: "Globe",
      linkHref: null,
      linkLabel: "Your municipal assembly",
    },
    {
      name: "Customary Authorities",
      desc: "Chiefs, families, and stool land custodians who hold traditional jurisdiction over customary land.",
      icon: "Users",
      linkHref: null,
      linkLabel: "Via district assemblies & stool secretariats",
    },
  ],
  costItems: [
    { item: "Land Purchase Price", typical: "Negotiated market rate" },
    { item: "Legal Fees", typical: "~1–2% of purchase value" },
    { item: "Stamp Duty", typical: "~0.5–1% of purchase price" },
    { item: "Lands Commission Fees", typical: "Set by commission" },
    { item: "Licensed Surveyor Fees", typical: "GHS 500–2,000+" },
  ],
  practicalTips: [
    "Always do a Lands Commission search before any payment",
    "Always use a qualified property lawyer",
    "Always obtain and verify an official site plan",
    "Always register your land immediately after purchase",
    "Never rush payment before completing due diligence",
    "Never rely on verbal agreements alone",
    "Always physically confirm boundaries on-site",
    "Always verify seller identity with an official government ID",
  ],
  redFlags: [
    "Seller is rushing you to close the deal quickly",
    "No documentation or ownership papers available",
    '"Cheap land" offer that sounds too good to be true',
    "Seller refuses to allow a Lands Commission search",
    "Multiple people claiming ownership of the same plot",
    "No site plan or official survey document exists",
  ],
  faqs: [
    {
      q: "What is the most important step before buying land in Ghana?",
      a: "The most critical step is conducting a Land Search at the Lands Commission. This verifies true ownership, checks for existing disputes, court orders, or government acquisition — before you pay a single pesewa.",
    },
    {
      q: "What is the difference between Title Registration and Deed Registration?",
      a: "Title Registration is government-guaranteed and offers stronger legal protection. Deed Registration only records the transaction — it does not guarantee ownership. Always aim for Title Registration.",
    },
    {
      q: "Can foreigners own land in Ghana?",
      a: "Foreigners can lease land in Ghana for up to 50 years. They cannot own land outright like Ghanaian citizens, who can hold leases of up to 99 years.",
    },
    {
      q: "What documents do I need to register land in Ghana?",
      a: "You need your Indenture (ownership document), a certified survey/site plan, valid national ID documents, and a stamp duty payment receipt from the Ghana Revenue Authority.",
    },
    {
      q: "What should I do if I have a land dispute?",
      a: "Do not use force. Gather all your documents, contact a property lawyer immediately, consider Alternative Dispute Resolution (ADR) or mediation first, and only go to court if the matter cannot be resolved otherwise.",
    },
  ],
  articles: [
    {
      id: "a-1",
      slug: "land-purchase-verification-1",
      title: "How to Buy Land Safely in Ghana (Step-by-Step Guide)",
      excerpt:
        "A comprehensive guide covering verification, documentation, legal review, and registration — everything you need to protect your land investment from day one.",
      type: "Guide",
      readTime: "8 min read",
      author: "InfoBoxx Editorial Desk",
      publishedAt: "Updated recently",
      coverImage:
        "https://images.unsplash.com/photo-1570129477492-45c003edd2be?auto=format&fit=crop&w=800&q=80",
      tags: ["Land Purchase", "Due Diligence", "Ghana"],
    },
    {
      id: "a-2",
      slug: "land-purchase-verification-2",
      title: "5 Mistakes That Make Ghanaians Lose Their Land",
      excerpt:
        "Learn the most common errors buyers make — from skipping the Lands Commission search to trusting verbal promises — and exactly how to protect yourself.",
      type: "Checklist",
      readTime: "5 min read",
      author: "Partner Knowledge Team",
      publishedAt: "This week",
      coverImage:
        "https://images.unsplash.com/photo-1589829545856-d10d557cf95f?auto=format&fit=crop&w=800&q=80",
      tags: ["Mistakes", "Red Flags", "Fraud Prevention"],
    },
    {
      id: "a-3",
      slug: "land-purchase-verification-3",
      title: "Land Title Registration in Ghana, Explained Simply",
      excerpt:
        "Understand the difference between title and deed registration, the five-step Lands Commission process, and why it legally protects your ownership for decades.",
      type: "Expert Explainer",
      readTime: "6 min read",
      author: "Consumer Guidance Unit",
      publishedAt: "Reviewed this month",
      coverImage:
        "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?auto=format&fit=crop&w=800&q=80",
      tags: ["Registration", "Title Deed", "Lands Commission"],
    },
  ],
  videos: [
    {
      id: "v-1",
      title: "What to Check Before Buying Land in Ghana",
      duration: "4:22",
      poster:
        "https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&w=800&q=80",
      description:
        "A visual walkthrough of the land verification process — from the initial Lands Commission search to documentation sign-off.",
    },
    {
      id: "v-2",
      title: "How to Avoid Land Fraud in Ghana",
      duration: "3:45",
      poster:
        "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=800&q=80",
      description:
        "Real case examples, warning signs, and actionable steps that protect buyers from fraudulent land transactions.",
    },
    {
      id: "v-3",
      title: "Stool Family Land: What Verification Looks Like",
      duration: "5:10",
      poster:
        "https://images.unsplash.com/photo-1540575467063-178a50c2df87?auto=format&fit=crop&w=800&q=80",
      description:
        "How customary grants differ on paper versus at the chief family level — and why your lawyer still needs the Lands Commission file.",
    },
    {
      id: "v-4",
      title: "Ground Rent & Lease Renewals Buyers Forget",
      duration: "3:58",
      poster:
        "https://images.unsplash.com/photo-1582268611958-ebfd161ef9cf?auto=format&fit=crop&w=800&q=80",
      description:
        "Quiet costs that reopen title risk if unpaid — auditors from our partner desks flag what billing cycles to insist on upfront.",
    },
    {
      id: "v-5",
      title: "Survey Plans & Boundary Pins in Plain Language",
      duration: "4:14",
      poster:
        "https://images.unsplash.com/photo-1500382017468-9049fed747ef?auto=format&fit=crop&w=800&q=80",
      description:
        "Licensed surveyors walk through stamping, overlaps, and when to redraw before you consolidate payment milestones.",
    },
  ],
  /** Media tab — video embeds/clips plus podcast episodes (CMS/API). */
  podcasts: [
    {
      id: "p-1",
      episodeLabel: "Episode 1",
      duration: "28 min",
      title: "Land Search First: Why the Lands Commission Is Non-Negotiable",
      description:
        "Lawyers unpack what a proper search reveals — encumbrances, disputes, stool grants — before you draft an offer or move money.",
      host: "Afrimore Legal Desk",
      publishedAt: "May 2026",
      coverImage:
        "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?auto=format&fit=crop&w=800&q=80",
    },
    {
      id: "p-2",
      episodeLabel: "Episode 2",
      duration: "35 min",
      title: "From Indenture to Title: Registration Steps in Plain English",
      description:
        "Walk through deed vs title registration, publication windows, and what your certificate actually protects once issued.",
      host: "Consumer Guidance Unit · Infoboxx",
      publishedAt: "April 2026",
      coverImage:
        "https://images.unsplash.com/photo-1589829545856-d10d557cf95f?auto=format&fit=crop&w=800&q=80",
    },
    {
      id: "p-3",
      episodeLabel: "Episode 3",
      duration: "22 min",
      title: "Lease Terms That Trip Up Buyers in Ghana",
      description:
        "Duration, renewal, ground rent, and what must be explicit in stool and family leases before you commit long term.",
      host: "GoldKey Property Desk",
      publishedAt: "March 2026",
      coverImage:
        "https://images.unsplash.com/photo-1591115765373-5207764f72e7?auto=format&fit=crop&w=800&q=80",
    },
    {
      id: "p-4",
      episodeLabel: "Episode 4",
      duration: "31 min",
      title: "Court vs ADR: Choosing a Dispute Path",
      description:
        "When mediation is faster than litigation, what evidence to assemble first, and timelines buyers should expect.",
      host: "Afrimore Legal Desk",
      publishedAt: "February 2026",
      coverImage:
        "https://images.unsplash.com/photo-1500382017468-9049fed747ef?auto=format&fit=crop&w=800&q=80",
    },
    {
      id: "p-5",
      episodeLabel: "Mini",
      duration: "14 min",
      title: "Insurance & Structural Checks Before Close",
      description:
        "Why some lenders require builder reports, and how indemnity riders can protect phased payments on serviced plots.",
      host: "GoldKey Property Desk",
      publishedAt: "January 2026",
      coverImage:
        "https://images.unsplash.com/photo-1582268611958-ebfd161ef9cf?auto=format&fit=crop&w=800&q=80",
    },
  ],
};
